import { useMemo } from "react";

import { type SearchReviewCandidate } from "@jobs/search-v2/searchV2CandidateResult";
import { SearchApplicationReviewModal } from "@jobs/search-v2/sheet/SearchApplicationReviewModal";

import { SheetScopeProvider } from "@/contexts/SheetScopeContext";
import { useGetJob } from "@/hooks/queries/useGetJob";
import { DashboardJob } from "@/types";
import { useSearchInterests } from "@/hooks/search/useSearchInterests";

export const DashboardSearchReviewModal = ({
  dashboardJob,
  onOpenChange,
}: {
  dashboardJob: DashboardJob;
  onOpenChange: (open: boolean) => void;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: job } = useGetJob({
    companyId: dashboardJob.companyId,
    jobId: dashboardJob.id,
  });
  const {
    deleteSearchInterest,
    searchInterests = [],
    upsertSearchInterest,
  } = useSearchInterests(dashboardJob.id);

  /* React Hooks */
  // Ids-only candidates — the sheet hydrates each user itself (via
  // includeCandidacyHydration), so no search result is needed here.
  const candidates = useMemo<SearchReviewCandidate[]>(
    () =>
      dashboardJob.searchInterestTalentIds.map((talentId) => ({
        talentId,
        userId: talentId,
      })),
    [dashboardJob.searchInterestTalentIds],
  );

  /* Handlers */
  const handleHide = (talentId: number) => {
    upsertSearchInterest({
      isInterested: false,
      jobId: dashboardJob.id,
      searchAnalytics: { source: "search_review_modal" },
      talentId,
    });
  };

  const handleShortlisted = (talentId: number) => {
    // Shortlisting consumes the interest — the talent now lives in the
    // pipeline instead.
    const existingInterest = searchInterests.find(
      (interest) => interest.talentId === talentId,
    );
    if (existingInterest) {
      deleteSearchInterest({
        jobId: dashboardJob.id,
        reason: "shortlist_cleanup",
        searchAnalytics: { source: "search_review_modal" },
        talentId,
      });
    }
  };

  /* Render */
  const render = () => {
    if (!job) return null;

    const hiddenTalentIds = new Set(
      searchInterests
        .filter((interest) => !interest.isInterested)
        .map((interest) => interest.talentId),
    );
    const visibleCandidates = candidates.filter(
      (candidate) => !hiddenTalentIds.has(candidate.talentId),
    );
    if (!visibleCandidates.length) return null;

    return (
      <SheetScopeProvider
        candidacyId=""
        companyId={dashboardJob.companyId}
        companyName={dashboardJob.companyName}
        job={job}
        jobId={dashboardJob.id}
      >
        <SearchApplicationReviewModal
          candidates={visibleCandidates}
          companyName={dashboardJob.companyName}
          includeCandidacyHydration
          job={job}
          onHide={handleHide}
          onOpenChange={onOpenChange}
          onShortlisted={handleShortlisted}
          open
        />
      </SheetScopeProvider>
    );
  };

  return render();
};
